import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Text } from '@cotera/client/app/components/ui';
import { Button } from '@cotera/client/v0/components/ui/button';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { useWatchableValue } from '@cotera/client/v0/actions/framework';
import { useWorkflowRunModel } from './workflow-run.model';
import {
  buildNodeSourceLabels,
  useAgentTitleById,
} from './hooks/use-upstream-source-labels';
import {
  resolveRunStepView,
  useWorkflowRunStepViewCatalog,
} from './workflow-run-step-view-catalog';
import type { WorkflowRunStepViewPayload } from './workflow-artifact-views';

/**
 * The one placeholder this view needs, instead of five near-identical blocks of
 * centred grey text. Names the step where it can, and offers a retry when the
 * failure is retryable — the payload comes from a query, so recovering was always
 * one `refetch()` away and there was no way to ask for it.
 */
const StepStatePanel: React.FC<{
  message: string;
  detail?: string;
  onRetry?: () => void;
}> = ({ message, detail, onRetry }) => (
  <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center">
    <Text.P className="text-foreground font-medium">{message}</Text.P>
    {detail !== undefined ? (
      <Text.Caption className="text-muted-foreground max-w-sm">
        {detail}
      </Text.Caption>
    ) : null}
    {onRetry !== undefined ? (
      <Button variant="outline" size="sm" className="mt-2" onClick={onRetry}>
        Try again
      </Button>
    ) : null}
  </div>
);

/**
 * In-panel stack body for a single run graph node: resolves the node + its run
 * data from the in-scope {@link WorkflowRunModel} graph and renders the step
 * view registered for its node type (default: JSON input/output). Shell chrome
 * (back button, breadcrumbs) lives on the host page.
 */
export const WorkflowRunStepStackContent: React.FC<{
  view: WorkflowRunStepViewPayload;
}> = ({ view }) => {
  const model = useWorkflowRunModel();
  const data = useWatchableValue(model.graphWatchable);
  const catalog = useWorkflowRunStepViewCatalog();
  const client = useTenantedClient();
  const orgId = useWhoami((s) => s.org.id);

  const node = data?.nodes.find((n) => n.nodeId === view.nodeId) ?? null;
  const stepView =
    node !== null ? resolveRunStepView(catalog, node.type) : null;

  // Friendly labels so a node-scoped ref renders its source with the agent's
  // title (e.g. `Gen Z Generator`) instead of the raw `Agent f79fea7f…`. The run
  // graph carries `agentId` on each node, so we map the same way the editor does.
  const agentTitleById = useAgentTitleById();
  const sourceLabels = useMemo(
    () =>
      data === null
        ? new Map<string, string>()
        : buildNodeSourceLabels(
            data.nodes.map((n) => ({
              id: n.nodeId,
              type: n.type,
              data: n.data,
              name: n.name,
            })),
            agentTitleById
          ),
    [data, agentTitleById]
  );
  const payloadQuery = useQuery({
    queryKey: queryKeys.workflow.runStepPayload({
      orgId,
      workflowId: model.workflowId,
      runId: model.runId,
      nodeId: view.nodeId,
    }),
    enabled: stepView?.needsPayload === true && node !== null,
    queryFn: async () => {
      const res = await client.resource.workflow.getRunStepPayload({
        workflowId: model.workflowId,
        runId: model.runId,
        nodeId: view.nodeId,
      });
      if (res.isErr()) {
        throw new Error(res.error.err.message ?? 'Failed to load step details');
      }
      return res.value;
    },
  });

  if (
    data === null ||
    (stepView?.needsPayload === true && payloadQuery.isLoading)
  ) {
    return <StepStatePanel message="Loading step\u2026" />;
  }

  // No node for this id, or no view registered for its type. Both mean the same
  // thing to whoever is looking at it — this step can't be shown — so they say so
  // once, in their words. "Step view not found" named an internal registry.
  if (node === null || stepView === null) {
    return (
      <StepStatePanel
        message="This step isn't part of the run any more."
        detail="It may have been removed from the workflow since this run finished."
      />
    );
  }

  if (stepView.needsPayload === true && payloadQuery.isError) {
    return (
      <StepStatePanel
        message={`Couldn't load ${node.name}`}
        detail="The run itself is fine — only these details failed to load."
        onRetry={() => void payloadQuery.refetch()}
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      {stepView.render({
        node,
        payload: payloadQuery.data ?? null,
        sourceLabels,
        ...(view.instanceKey !== undefined
          ? { initialInstanceKey: view.instanceKey }
          : {}),
      })}
    </div>
  );
};
